/**
 * AnchorFrameIterator (2026-02 · new feature)
 *
 * Operator paints intent on the FIRST FRAME of a Runway Aleph source
 * video using gpt-image-2 + a text prompt. The selected anchor frame
 * (raw R0 or any iteration R1/R2/R3) becomes the dominant visual
 * reference fed into Aleph's v2v generation.
 *
 * Shared between:
 *   - VideoWorkbench.js (Stage 7 / per-shot)
 *   - RunwayAlephRecipe.js (standalone /recipes/runway-aleph)
 *
 * Contract:
 *   props:
 *     - mode: "shot" | "recipe" — picks the API base path
 *     - shotId | runId — identifier scoped to the mode
 *     - r0Url: the raw first-frame URL (auto-extracted on video upload)
 *     - iterations: existing iteration array from server (each:
 *         { url, round, prompt, quality, created_at })
 *     - selectedUrl: the currently-anchored URL (controlled by parent)
 *     - onSelect(url): parent updates the anchor URL
 *     - onIterationsChanged(newArr): parent refreshes its copy of the
 *                                     server-side iteration list
 *     - disabled: pause generate button during parent's own busy state
 *
 * The component is "dumb" w.r.t. selection — parent owns ``selectedUrl``
 * so the Aleph generate call has a single source of truth.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, Check, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { toast } from '../utils/toast';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AnchorFrameIterator({
  mode,
  shotId,
  runId,
  r0Url,
  iterations = [],
  selectedUrl,
  onSelect,
  onIterationsChanged,
  disabled = false,
}) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);

  // ── API routing ─────────────────────────────────────────────────────
  const iteratePath =
    mode === 'recipe'
      ? `${API}/api/recipes/runway-aleph/iterate-anchor-frame`
      : `${API}/api/shots/${shotId}/anchor-frame/iterate`;
  const revertPath =
    mode === 'recipe'
      ? null // no revert endpoint on the recipe side yet — drop button there
      : `${API}/api/shots/${shotId}/anchor-frame/revert`;

  // The "base" iteration source: latest iteration if any, else r0
  const lastIter = iterations[iterations.length - 1];
  const baseImage = lastIter?.url || r0Url;

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Type a prompt first');
      return;
    }
    if (!baseImage) {
      toast.error('No source frame to iterate on');
      return;
    }
    setBusy(true);
    try {
      const payload =
        mode === 'recipe'
          ? { run_id: runId, image_url: baseImage, prompt: prompt.trim() }
          : { image_url: baseImage, prompt: prompt.trim() };
      const res = await axios.post(iteratePath, payload);
      const newIter = {
        url: res.data.url,
        round: res.data.round,
        prompt: res.data.prompt,
        source_url: baseImage,
      };
      const newArr = [...iterations, newIter];
      onIterationsChanged?.(newArr);
      // Auto-select the new iteration as the active anchor — operator's
      // intent was to make this one, so make it the default pick.
      onSelect?.(newIter.url);
      setPrompt('');
      toast.success(`Anchor R${newIter.round} ready`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anchor iteration failed');
    } finally {
      setBusy(false);
    }
  };

  const handleClearAll = async () => {
    if (!revertPath) return;
    try {
      await axios.post(revertPath);
      onIterationsChanged?.([]);
      // If the now-cleared iteration was the active anchor, drop it back
      // to R0 (or null if no R0 either).
      if (selectedUrl && iterations.some((i) => i.url === selectedUrl)) {
        onSelect?.(r0Url || null);
      }
      toast.success('Anchor iterations cleared');
    } catch {
      toast.error('Failed to clear');
    }
  };

  if (!r0Url && iterations.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-white/10 p-4 text-center text-[11px] text-white/40"
        data-testid="anchor-frame-empty"
      >
        Upload a source video to start painting the anchor frame.
      </div>
    );
  }

  // Helper to render a single thumbnail card (R0 or any iteration).
  const renderCard = (url, label, sublabel, isR0 = false) => {
    const isSelected = selectedUrl === url;
    return (
      <button
        type="button"
        key={url}
        onClick={() => onSelect?.(url)}
        className="relative rounded-lg overflow-hidden border-2 transition-all text-left cursor-pointer"
        style={{
          borderColor: isSelected ? '#a78bfa' : 'rgba(167,139,250,0.2)',
          boxShadow: isSelected ? '0 0 14px rgba(167,139,250,0.35)' : 'none',
          background: 'rgba(0,0,0,0.3)',
        }}
        data-testid={`anchor-frame-card-${isR0 ? 'r0' : `r${label}`}`}
      >
        <div className="flex items-center justify-between px-2 py-1 border-b border-white/5 text-[9px]">
          <span
            className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
            style={{
              background: isR0 ? 'rgba(167,139,250,0.18)' : 'rgba(167,139,250,0.12)',
              color: '#c4b5fd',
            }}
          >
            {isR0 ? 'R0 · Frame' : `R${label}`}
          </span>
          {sublabel && (
            <span className="text-white/30 truncate ml-1.5" title={sublabel}>
              "{sublabel.slice(0, 30)}{sublabel.length > 30 ? '…' : ''}"
            </span>
          )}
          {isSelected && <Check className="w-3 h-3 ml-1.5 text-violet-300 shrink-0" />}
        </div>
        {/* Use ``object-contain`` (not cover) so the FULL frame is always
            visible — operator MUST see the whole composition to make a
            meaningful pick. Letterbox bars on the sides/top are
            acceptable and expected for portrait subjects.
            The aspect-video wrapper guarantees a clean 16:9 thumb box
            regardless of source frame aspect. */}
        <div className="w-full bg-black flex items-center justify-center" style={{ aspectRatio: '16 / 9' }}>
          <img
            src={url}
            alt={isR0 ? 'Anchor R0' : `Anchor R${label}`}
            className="max-w-full max-h-full object-contain"
            loading="lazy"
          />
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="anchor-frame-iterator">
      <div className="flex items-center gap-1.5">
        <ImageIcon className="w-3.5 h-3.5 text-violet-300/70" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">
          Anchor Frame · gpt-image-2
        </span>
        <span className="ml-auto text-[9px] text-white/30">
          {iterations.length} iter{iterations.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Stack — R0 first, then iterations newest-on-top */}
      <div className="grid grid-cols-2 gap-2">
        {r0Url && renderCard(r0Url, 0, 'first frame of source video', true)}
        {[...iterations].reverse().map((iter) =>
          renderCard(iter.url, iter.round, iter.prompt)
        )}
      </div>

      {/* Prompt + Generate */}
      <div className="space-y-1.5">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Edit the anchor frame… e.g. 'change wardrobe to navy blazer, soft golden-hour light'"
          rows={2}
          disabled={busy || disabled}
          className="w-full px-2.5 py-1.5 rounded-lg bg-black/30 border border-white/10 text-[11px] text-white/80 placeholder-white/30 focus:outline-none focus:border-violet-500/50 resize-none"
          data-testid="anchor-frame-prompt-input"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={busy || disabled || !prompt.trim() || !baseImage}
            className="flex-1 py-2 rounded-lg text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background:
                'linear-gradient(135deg, rgba(167,139,250,0.95), rgba(139,92,246,0.95))',
              color: 'white',
            }}
            data-testid="anchor-frame-generate-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Iterating…
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3" />
                Iterate {iterations.length > 0 ? `→ R${iterations.length + 1}` : '→ R1'}
              </>
            )}
          </button>
          {revertPath && iterations.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              disabled={busy || disabled}
              title="Clear all anchor iterations (keeps R0)"
              className="px-2.5 py-2 rounded-lg border border-red-500/30 text-red-400/80 hover:bg-red-500/10 transition-all"
              data-testid="anchor-frame-revert-btn"
            >
              <RotateCcw className="w-3 h-3" />
            </button>
          )}
        </div>
        <p className="text-[9px] text-white/30 leading-snug">
          The selected card becomes the dominant identity anchor for Runway Aleph.
          Click any card to switch which one Aleph uses as the visual target.
          {selectedUrl && (
            <span className="block mt-1 text-violet-300/60">
              ✓ When you hit Generate, Aleph is told to <em>start on this frame</em> —
              wardrobe, environment, and lighting from the picked card carry into the video.
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
